import { supabase } from "@/lib/supabase";
// @wc-ignore-file
// (RPC names here are data, not user-facing copy.)
import type { Database } from "@shared/types/supabase.types";

/**
 * One row of list_platform_workspaces() (CR-116), the only data source of the
 * superuser Workspaces page since CR-178. Billing columns come from a LEFT
 * JOIN, so they are null for a workspace without billing (the nullable shape is
 * restated in the supabase.types.ts override).
 */
export type PlatformWorkspace =
  Database["public"]["Functions"]["list_platform_workspaces"]["Returns"][number];

/** Every live workspace on the platform (admin-only RPC; raises 42501 otherwise). */
export async function listPlatformWorkspaces(): Promise<PlatformWorkspace[]> {
  const { data, error } = await supabase.rpc("list_platform_workspaces");

  if (error) {
    throw error;
  }

  return data ?? [];
}

/**
 * CR-191: hard-delete a workspace and everything under it (admin-only RPC,
 * audit-logged as workspace_delete). `confirmName` must equal the workspace
 * name exactly. The PostgREST error is thrown as-is so the caller can map its
 * `code`: 42501 not an admin, P0002 not found, 22023 name mismatch, 55006
 * AI usage not settled with Hyperline yet (pending / in_flight queue rows,
 * CR-256), 23502/23503 rows that still block the cascade (CR-228).
 */
export async function deletePlatformWorkspace({
  workspaceId,
  confirmName,
}: {
  workspaceId: string;
  confirmName: string;
}): Promise<void> {
  const { error } = await supabase.rpc("delete_platform_workspace", {
    p_workspace_id: workspaceId,
    p_confirm_name: confirmName,
  });

  if (error) {
    throw error;
  }
}

export type DeleteWorkspaceErrorCode =
  | "forbidden"
  | "not_found"
  | "name_mismatch"
  | "unsettled_usage"
  | "blocked"
  | "failed";

/** Maps a deletePlatformWorkspace error to the reason the UI explains (Postgres error code → reason). */
export function deleteWorkspaceErrorCode(error: unknown): DeleteWorkspaceErrorCode {
  const code =
    error && typeof error === "object" && "code" in error && typeof error.code === "string"
      ? error.code
      : null;
  switch (code) {
    case "42501":
      return "forbidden";
    case "P0002":
      return "not_found";
    case "22023":
      return "name_mismatch";
    case "55006":
      return "unsettled_usage";
    case "23502":
    case "23503":
      return "blocked";
    default:
      return "failed";
  }
}
